import { gql, useQuery, useSubscription } from '@apollo/client';
import { meQuery } from 'graphql/queries';
import { title, titlePrefix } from 'lib/properties';
import useNotificationService from 'pages/Game/utils/NotificationService';
import { useTranslation } from 'react-i18next';
import { usePageVisibility } from 'react-page-visibility';
import { useMethods } from 'react-use';
import {
  GameInvitations,
  GameInvitationsVariables,
  GameTypeEnum,
  LobbyInvitation,
  Me,
} from '../../../graphql/types';

export interface InvitationsState {
  loading: boolean;
  error: Error | null;
  invitations: LobbyInvitation[] | null;
}

const initialState: InvitationsState = {
  invitations: null,
  loading: true,
  error: null,
};

const createMethods = (state: InvitationsState) => ({
  load(invitations: LobbyInvitation[]) {
    return {
      invitations,
      loading: false,
      error: null,
    };
  },
  add(invitation: LobbyInvitation) {
    if (state.loading || state.invitations == null) {
      throw new Error('Unable to add invitation when still loading');
    }
    return {
      ...state,
      invitations: state.invitations.concat(invitation),
    };
  },
  remove(id: string) {
    if (state.loading || state.invitations == null) {
      throw new Error('Unable to add invitation when still loading');
    }
    return {
      ...state,
      invitations: state.invitations.filter(
        (invitation) => invitation.id !== id,
      ),
    };
  },
  error(err: Error) {
    return { ...state, error: err, loading: false };
  },
});

export default function useInvitations(gameType?: GameTypeEnum) {
  const [state, methods] = useMethods(createMethods, initialState);
  const { data: userData } = useQuery<Me>(meQuery, {
    fetchPolicy: 'cache-only',
    returnPartialData: true,
    errorPolicy: 'ignore',
    notifyOnNetworkStatusChange: true,
  });
  const { notify } = useNotificationService();
  const [t] = useTranslation();
  const isVisible = usePageVisibility();

  const { error } = useSubscription<GameInvitations, GameInvitationsVariables>(
    invitationsSubscription,
    {
      variables: {
        input: { gameType },
      },
      onSubscriptionData({ subscriptionData: { data } }) {
        const event = data?.gameInvitations;
        if (event == null) {
          return;
        }
        switch (event.__typename) {
          case 'GameInvitations':
            methods.load(event.invitations);
            break;
          case 'GameInvitationAdded':
            notify(
              t('notification.invitation.title', 'Pozvánka do hry'),
              t('notification.invitation.text', `{{name}} Vás zve do hry`, {
                name: event.added.inviter.name ?? '',
              }),
              userData?.me?.notificationSound,
              false,
              6000,
            );
            methods.add(event.added);
            break;
          case 'GameInvitationRemoved':
            methods.remove(event.removed);
            break;
        }
      },
    },
  );
  if (error && !state.error) {
    methods.error(error);
  }

  if (!isVisible && state && !!state.invitations?.length) {
    if (!document.title.includes(titlePrefix)) {
      document.title = `${titlePrefix}${document.title}`;
    }
  } else {
    if (document.title.includes(titlePrefix)) {
      document.title = title;
    }
  }

  return state;
}

const invitationsSubscription = gql`
  subscription GameInvitations($input: GameInvitationsInput!) {
    gameInvitations(input: $input) {
      ... on GameInvitations {
        invitations {
          ...LobbyInvitation
        }
      }
      ... on GameInvitationAdded {
        added {
          ...LobbyInvitation
        }
      }
      ... on GameInvitationRemoved {
        removed
      }
    }
  }
  fragment LobbyInvitation on GameInvitation {
    id
    inviter {
      id
      email
      name
    }
  }
`;
